import React, { useMemo, useState } from 'react';
import { XIcon } from '@heroicons/react/outline';

function slugify(input) {
  return (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function ToDoSettings({
  open,
  onClose,
  statuses,
  categories,
  upsertStatus,
  deleteStatus,
  upsertCategory,
  deleteCategory,
  loading,
}) {
  const maxStatusOrder = useMemo(() => {
    return statuses.reduce((acc, s) => Math.max(acc, Number.isFinite(s.order) ? s.order : 0), 0);
  }, [statuses]);

  const maxCategoryOrder = useMemo(() => {
    return categories.reduce((acc, c) => Math.max(acc, Number.isFinite(c.order) ? c.order : 0), 0);
  }, [categories]);

  const [newStatusLabel, setNewStatusLabel] = useState('');
  const [newStatusId, setNewStatusId] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#64748b');

  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3b82f6');

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-[92%] sm:w-[28rem] bg-white shadow-basic border-l p-4 overflow-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">ToDo Settings</div>
            <div className="text-sm text-neutral-600">Statuses & categories</div>
          </div>
          <button type="button" className="p-2 rounded-lg hover:bg-slate-100" onClick={onClose}>
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mt-6">
          <div className="text-sm font-semibold">Statuses</div>
          <div className="text-xs text-neutral-600 mt-1">
            Board columns are driven by statuses. Reordering changes column order.
          </div>

          <div className="mt-3 space-y-2">
            {statuses.length === 0 ? (
              <div className="text-sm text-neutral-500">No statuses</div>
            ) : (
              statuses.map((s) => (
                <ConfigRow
                  key={s.id}
                  id={s.id}
                  label={s.label}
                  color={s.color}
                  order={s.order}
                  disabled={loading}
                  onSave={(patch) => upsertStatus({ id: s.id, ...patch })}
                  onDelete={() => deleteStatus(s.id)}
                  onMoveUp={async () => {
                    const idx = statuses.findIndex((x) => x.id === s.id);
                    if (idx <= 0) return;
                    const above = statuses[idx - 1];
                    await Promise.all([
                      upsertStatus({ id: above.id, label: above.label, color: above.color, order: s.order ?? 0 }),
                      upsertStatus({ id: s.id, label: s.label, color: s.color, order: above.order ?? 0 }),
                    ]);
                  }}
                  onMoveDown={async () => {
                    const idx = statuses.findIndex((x) => x.id === s.id);
                    if (idx < 0 || idx >= statuses.length - 1) return;
                    const below = statuses[idx + 1];
                    await Promise.all([
                      upsertStatus({ id: below.id, label: below.label, color: below.color, order: s.order ?? 0 }),
                      upsertStatus({ id: s.id, label: s.label, color: s.color, order: below.order ?? 0 }),
                    ]);
                  }}
                />
              ))
            )}
          </div>

          <div className="mt-4 bg-slate-50 border rounded-lg p-3">
            <div className="text-sm font-medium">Add status</div>
            <div className="mt-2 grid grid-cols-1 gap-2">
              <input
                className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                placeholder="Label (e.g. QA Review)"
                value={newStatusLabel}
                onChange={(e) => {
                  setNewStatusLabel(e.target.value);
                  if (!newStatusId) setNewStatusId(slugify(e.target.value));
                }}
                disabled={loading}
              />
              <input
                className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                placeholder="ID (optional, e.g. qa_review)"
                value={newStatusId}
                onChange={(e) => setNewStatusId(e.target.value)}
                disabled={loading}
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newStatusColor}
                  onChange={(e) => setNewStatusColor(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="ml-auto px-3 py-2 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                  disabled={loading || !newStatusLabel.trim()}
                  onClick={async () => {
                    const id = (newStatusId || slugify(newStatusLabel)) || undefined;
                    const order = (maxStatusOrder ?? 0) + 1;
                    await upsertStatus({ id, label: newStatusLabel.trim(), color: newStatusColor, order });
                    setNewStatusLabel('');
                    setNewStatusId('');
                    setNewStatusColor('#64748b');
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="text-sm font-semibold">Categories</div>
          <div className="text-xs text-neutral-600 mt-1">Optional grouping for filtering and badges.</div>

          <div className="mt-3 space-y-2">
            {categories.length === 0 ? (
              <div className="text-sm text-neutral-500">No categories</div>
            ) : (
              categories
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((c) => (
                  <ConfigRow
                    key={c.id}
                    id={c.id}
                    label={c.label}
                    color={c.color}
                    order={c.order}
                    disabled={loading}
                    onSave={(patch) => upsertCategory({ id: c.id, ...patch })}
                    onDelete={() => deleteCategory(c.id)}
                  />
                ))
            )}
          </div>

          <div className="mt-4 bg-slate-50 border rounded-lg p-3">
            <div className="text-sm font-medium">Add category</div>
            <div className="mt-2 grid grid-cols-1 gap-2">
              <input
                className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                placeholder="Label (e.g. Finance)"
                value={newCategoryLabel}
                onChange={(e) => {
                  setNewCategoryLabel(e.target.value);
                  if (!newCategoryId) setNewCategoryId(slugify(e.target.value));
                }}
                disabled={loading}
              />
              <input
                className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                placeholder="ID (optional, e.g. finance)"
                value={newCategoryId}
                onChange={(e) => setNewCategoryId(e.target.value)}
                disabled={loading}
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newCategoryColor}
                  onChange={(e) => setNewCategoryColor(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="ml-auto px-3 py-2 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                  disabled={loading || !newCategoryLabel.trim()}
                  onClick={async () => {
                    const id = (newCategoryId || slugify(newCategoryLabel)) || undefined;
                    const order = (maxCategoryOrder ?? 0) + 1;
                    await upsertCategory({ id, label: newCategoryLabel.trim(), color: newCategoryColor, order });
                    setNewCategoryLabel('');
                    setNewCategoryId('');
                    setNewCategoryColor('#3b82f6');
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigRow({ id, label, color, order, onSave, onDelete, onMoveUp, onMoveDown, disabled }) {
  const [draftLabel, setDraftLabel] = useState(label || '');
  const [draftColor, setDraftColor] = useState(color || '#94a3b8');
  const [draftOrder, setDraftOrder] = useState(Number.isFinite(order) ? order : 0);
  const [saving, setSaving] = useState(false);

  async function save() {
    try {
      setSaving(true);
      await onSave?.({ label: draftLabel.trim(), color: draftColor, order: Number(draftOrder) || 0 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border rounded-lg p-2 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-neutral-500 truncate" title={id}>
          {id}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {onMoveUp && (
            <button
              type="button"
              className="px-2 py-1 rounded-md text-xs border hover:bg-slate-50 disabled:opacity-60"
              onClick={onMoveUp}
              disabled={disabled || saving}
              title="Move up"
            >
              ↑
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              className="px-2 py-1 rounded-md text-xs border hover:bg-slate-50 disabled:opacity-60"
              onClick={onMoveDown}
              disabled={disabled || saving}
              title="Move down"
            >
              ↓
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-7">
          <input
            className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            disabled={disabled || saving}
          />
        </div>
        <div className="col-span-2">
          <input
            type="color"
            value={draftColor}
            onChange={(e) => setDraftColor(e.target.value)}
            disabled={disabled || saving}
            className="w-full h-9"
          />
        </div>
        <div className="col-span-3">
          <input
            className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
            value={draftOrder}
            onChange={(e) => setDraftOrder(e.target.value)}
            disabled={disabled || saving}
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="px-3 py-2 rounded-lg text-sm border bg-white hover:bg-slate-50 disabled:opacity-60"
          disabled={disabled || saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {onDelete && (
          <button
            type="button"
            className="px-3 py-2 rounded-lg text-sm border bg-white hover:bg-red-50 text-red-600 disabled:opacity-60"
            disabled={disabled || saving}
            onClick={async () => {
              const ok = window.confirm(`Delete "${label || id}"? Existing todos will keep the old value.`);
              if (!ok) return;
              await onDelete();
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default ToDoSettings;
